import { useState, useEffect } from "react";
import { 
  Users, Plus, Edit2, Trash2, Phone, Mail, 
  Building2, Save, X, UserCheck
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RicambistaOperators({ user }) {
  const [loading, setLoading] = useState(true);
  const [operators, setOperators] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [opRes, assignRes] = await Promise.all([
        authFetch(`${API}/ricambista/operators`, { }),
        authFetch(`${API}/ricambista/operators/assignments`, { })
      ]);
      
      if (opRes.ok) setOperators(await opRes.json());
      if (assignRes.ok) setAssignments(await assignRes.json());
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Inserisci il nome dell'operatore");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/ricambista/operators`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success("Operatore creato");
        setShowCreateDialog(false);
        setFormData({ name: "", email: "", phone: "", notes: "" });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      toast.error("Inserisci il nome dell'operatore");
      return;
    }
    setSubmitting(true);
    try {
      const res = await authFetch(`${API}/ricambista/operators/${selectedOperator.operator_id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        toast.success("Operatore aggiornato");
        setShowEditDialog(false);
        setSelectedOperator(null);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (operator) => {
    if (!confirm(`Eliminare l'operatore "${operator.name}"? Verrà rimosso da tutte le officine assegnate.`)) {
      return;
    }
    try {
      const res = await authFetch(`${API}/ricambista/operators/${operator.operator_id}`, {
        method: "DELETE"
      });
      if (res.ok) {
        toast.success("Operatore eliminato");
        fetchData();
      } else {
        toast.error("Errore nell'eliminazione");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  const handleAssignOperator = async (officinaId, operatorName) => {
    try {
      const res = await authFetch(`${API}/ricambista/officine/assign-operator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          officina_id: officinaId,
          operator_name: operatorName
        })
      });
      if (res.ok) {
        toast.success("Operatore assegnato");
        fetchData();
      } else {
        toast.error("Errore nell'assegnazione");
      }
    } catch (e) {
      toast.error("Errore di connessione");
    }
  };

  const openEditDialog = (operator) => {
    setSelectedOperator(operator);
    setFormData({
      name: operator.name || "",
      email: operator.email || "",
      phone: operator.phone || "",
      notes: operator.notes || ""
    });
    setShowEditDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operators-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="w-7 h-7 text-primary" />
            Archivio Operatori
          </h1>
          <p className="text-zinc-400 mt-1">
            Gestisci gli operatori e assegnali alle officine
          </p>
        </div>
        <Button onClick={() => {
          setFormData({ name: "", email: "", phone: "", notes: "" });
          setShowCreateDialog(true);
        }}>
          <Plus className="w-4 h-4 mr-2" />
          Nuovo Operatore
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card p-4 border-l-4 border-l-primary">
          <p className="text-zinc-400 text-sm">Operatori Totali</p>
          <p className="text-2xl font-bold text-white">{operators.length}</p>
        </div>
        <div className="card p-4 border-l-4 border-l-green-500">
          <p className="text-zinc-400 text-sm">Officine Assegnate</p>
          <p className="text-2xl font-bold text-white">
            {assignments.filter(a => a.assigned_operator).length}
          </p>
        </div>
        <div className="card p-4 border-l-4 border-l-yellow-500">
          <p className="text-zinc-400 text-sm">Officine Non Assegnate</p>
          <p className="text-2xl font-bold text-white">
            {assignments.filter(a => !a.assigned_operator).length}
          </p>
        </div>
      </div>

      {/* Operators List */}
      <div className="card">
        <div className="p-4 border-b border-zinc-800">
          <h3 className="font-semibold text-white">Lista Operatori</h3>
        </div>
        {operators.length === 0 ? (
          <div className="p-8 text-center text-zinc-500">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nessun operatore ancora</p>
            <p className="text-sm mt-1">Crea il primo operatore per iniziare</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800">
            {operators.map((op) => (
              <div key={op.operator_id} className="p-4 flex items-center justify-between hover:bg-zinc-800/30">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-sm flex items-center justify-center">
                    <UserCheck className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{op.name}</p>
                    <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                      {op.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {op.email}
                        </span>
                      )}
                      {op.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {op.phone}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right mr-4">
                    <p className="text-sm text-zinc-400">Officine assegnate</p>
                    <p className="text-lg font-bold text-primary">{op.assigned_officine_count || 0}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => openEditDialog(op)}>
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(op)} className="text-red-400 hover:text-red-300">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assignments Table */}
      <div className="card">
        <div className="p-4 border-b border-zinc-800">
          <h3 className="font-semibold text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Assegnazioni Officine
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Officina</th>
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Città</th>
                <th className="text-left p-3 text-xs text-zinc-400 font-medium">Operatore Assegnato</th>
                <th className="text-center p-3 text-xs text-zinc-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {assignments.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-zinc-500">
                    Nessuna officina collegata
                  </td>
                </tr>
              ) : (
                assignments.map((off) => (
                  <tr key={off.user_id} className="hover:bg-zinc-800/30">
                    <td className="p-3">
                      <p className="text-white">{off.company_name}</p>
                    </td>
                    <td className="p-3 text-zinc-400">{off.city}</td>
                    <td className="p-3">
                      <Select
                        value={off.assigned_operator || "none"}
                        onValueChange={(value) => handleAssignOperator(off.user_id, value === "none" ? "" : value)}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue placeholder="Seleziona operatore" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-- Nessuno --</SelectItem>
                          {operators.map((op) => (
                            <SelectItem key={op.operator_id} value={op.name}>
                              {op.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-3 text-center">
                      {off.assigned_operator ? (
                        <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                          Assegnato
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">
                          Da assegnare
                        </span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Nuovo Operatore
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nome *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: Mario Rossi"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario@email.it"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Telefono</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Note</label>
              <textarea
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleCreate} disabled={submitting}>
              {submitting ? "Creazione..." : "Crea Operatore"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-primary" />
              Modifica Operatore
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Nome *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Es: Mario Rossi"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Email</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="mario@email.it"
                />
              </div>
              <div>
                <label className="text-sm text-zinc-400 mb-1 block">Telefono</label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+39 333 1234567"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-zinc-400 mb-1 block">Note</label>
              <textarea
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Note aggiuntive..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleUpdate} disabled={submitting}>
              {submitting ? "Salvataggio..." : "Salva Modifiche"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
